import React, { Component, createRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import classes from "./Registration.module.css";
import axios from "../../../axios";

// Wrapper to use hooks with class component
function withRouter(Component) {
  return function WrappedComponent(props) {
    const [searchParams] = useSearchParams();
    return <Component {...props} searchParams={searchParams} />;
  };
}

class Registration extends Component {
  constructor(props) {
    super(props);
    this.emailRef = createRef();
    this.passwordRef = createRef();
    this.confirmPasswordRef = createRef();
    this.firstNameRef = createRef();
    this.lastNameRef = createRef();
    this.companyNameRef = createRef();
  }

  state = {
    loading: false,
    error: null,
    success: false,
    redirectUrl: null,
    submittedEmail: ""
  };

  componentDidMount() {
    const redirectUrl = this.props.searchParams.get("redirect");
    this.setState({ redirectUrl });
  }

  validateForm = () => {
    const email = this.emailRef.current?.value || "";
    const password = this.passwordRef.current?.value || "";
    const confirmPassword = this.confirmPasswordRef.current?.value || "";
    const companyName = this.companyNameRef.current?.value || "";

    if (!email || !password || !companyName) {
      this.setState({ error: "Please fill in all required fields" });
      return false;
    }

    if (password !== confirmPassword) {
      this.setState({ error: "Passwords do not match" });
      return false;
    }

    if (password.length < 6) {
      this.setState({ error: "Password must be at least 6 characters" });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.setState({ error: "Please enter a valid email address" });
      return false;
    }

    return true;
  };

  handleSubmit = async (event) => {
    event.preventDefault();

    if (!this.validateForm()) return;

    this.setState({ loading: true, error: null });

    const email = this.emailRef.current.value;
    const password = this.passwordRef.current.value;
    const firstName = this.firstNameRef.current?.value || "";
    const lastName = this.lastNameRef.current?.value || "";
    const companyName = this.companyNameRef.current.value;
    const { redirectUrl } = this.state;

    try {
      await axios.post("/registration/register", {
        email,
        password,
        firstName,
        lastName,
        companyName,
        redirectUrl
      });

      this.setState({
        loading: false,
        success: true,
        submittedEmail: email
      });
    } catch (err) {
      let errorMessage = "Registration failed. Please try again.";
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      this.setState({
        loading: false,
        error: errorMessage
      });
    }
  };

  render() {
    const { redirectUrl, loading, error, success, submittedEmail } = this.state;

    // No redirect URL - registration not allowed directly
    if (!redirectUrl) {
      return (
        <div className={classes.pageContainer}>
          <div className={classes.messageContainer}>
            <h1 className={classes.title}>Registration Not Available</h1>
            <p className={classes.message}>
              Registration is only possible when redirected from one of our platforms.
            </p>
            <p className={classes.submessage}>
              Please visit <strong>shop.allorado.io</strong> or <strong>device.allorado.io</strong> to register.
            </p>
            <Link to="/" className={classes.backLink}>
              ← Back to Login
            </Link>
          </div>
        </div>
      );
    }

    // Success state
    if (success) {
      return (
        <div className={classes.pageContainer}>
          <div className={classes.messageContainer}>
            <div className={classes.successIcon}>✓</div>
            <h1 className={classes.title}>Check Your Email</h1>
            <p className={classes.message}>
              We've sent a verification link to <strong>{submittedEmail}</strong>
            </p>
            <p className={classes.submessage}>
              Please click the link in the email to verify your account and complete registration.
            </p>
          </div>
        </div>
      );
    }

    // Registration form
    return (
      <div className={classes.pageContainer}>
        <div className={classes.formContainer}>
          <h1 className={classes.title}>Create Account</h1>
          <p className={classes.subtitle}>
            Register to access our platform
          </p>

          <form onSubmit={this.handleSubmit}>
            <div className={classes.row}>
              <input
                type="text"
                placeholder="First Name"
                className={classes.inputField}
                ref={this.firstNameRef}
                disabled={loading}
              />
              <input
                type="text"
                placeholder="Last Name"
                className={classes.inputField}
                ref={this.lastNameRef}
                disabled={loading}
              />
            </div>

            <input
              type="email"
              placeholder="Email *"
              className={classes.inputField}
              ref={this.emailRef}
              disabled={loading}
            />

            <input
              type="text"
              placeholder="Company Name *"
              className={classes.inputField}
              ref={this.companyNameRef}
              disabled={loading}
            />

            <input
              type="password"
              placeholder="Password *"
              className={classes.inputField}
              ref={this.passwordRef}
              disabled={loading}
              autoComplete="new-password"
            />

            <input
              type="password"
              placeholder="Confirm Password *"
              className={classes.inputField}
              ref={this.confirmPasswordRef}
              disabled={loading}
              autoComplete="new-password"
            />

            {error && (
              <div className={classes.errorContainer}>
                <p>{error}</p>
              </div>
            )}

            <button
              type="submit"
              className={classes.submitButton}
              disabled={loading}
            >
              {loading ? <span className={classes.spinner}></span> : "Create Account"}
            </button>
          </form>
        </div>
      </div>
    );
  }
}

export default withRouter(Registration);
